import type { Route } from ".react-router/types/app/routes/+types/_storefront._products.products";
import difference from "lodash/difference";
import isEqual from "lodash/isEqual";
import { useCallback } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useLoaderData } from "react-router";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { productListFiltersRemovedEvent } from "~/tooling/telemetry/events";
import type { FiltersConfig, FiltersState, RemoveFilterFn } from "./types";

type FiltersForm = UseFormReturn<FiltersState>;

export const useFilters = (form: FiltersForm, withTracking = false) => {
	const loaderData = useLoaderData<Route.ComponentProps["loaderData"]>();

	const initialFilters = createInitialFilters();
	const { trackFiltersRemoved } = useFiltersTracking(loaderData.filterConfig);

	const setFilter = useCallback(
		<K extends keyof FiltersState>(
			key: keyof FiltersState,
			value: FiltersState[K],
			shouldTrack = withTracking,
		) => {
			const newValue =
				Array.isArray(value) && value.length === 0 ? undefined : value;

			if (shouldTrack) {
				trackFiltersRemoved(form.getValues(), { [key]: newValue });
			}

			form.setValue(key, newValue, { shouldValidate: true });
		},
		[form.setValue, withTracking, trackFiltersRemoved, form.getValues],
	);

	const setBulkFilters = useCallback(
		(updates: Partial<FiltersState>, clearedAllFilters = false) => {
			if (withTracking) {
				trackFiltersRemoved(form.getValues(), updates, clearedAllFilters);
			}

			Object.entries(updates).forEach(([key, value]) => {
				setFilter(
					key as keyof FiltersState,
					value as FiltersState[keyof FiltersState],
					false,
				);
			});
		},
		[setFilter, form.getValues, withTracking, trackFiltersRemoved],
	);

	const clearAllFilters = () => {
		const clearedEntries = Object.entries(form.getValues()).map(([key]) => [
			key,
			initialFilters[key as keyof FiltersState],
		]);

		const valueOverrides = {
			topProducersLowPrice: false,
		};

		const newValues = {
			...Object.fromEntries(clearedEntries),
			...valueOverrides,
		};

		if (withTracking) {
			trackFiltersRemoved(form.getValues(), newValues, true);
		}

		form.reset(newValues);
	};

	const removeFilter: RemoveFilterFn = (category, value = undefined) => {
		const currentValue = form.getValues(category);

		if (Array.isArray(currentValue) && typeof value === "string") {
			const newValue = currentValue.filter((v) => v.toString() !== value);

			// Keep original types for number arrays (producers, category, rating)
			const isNumberArray =
				currentValue.length > 0 && typeof currentValue[0] === "number";
			const typedValue: string[] | number[] = isNumberArray
				? (newValue as number[])
				: newValue.map((v) => v.toString());

			return setFilter(category, typedValue.length ? typedValue : undefined);
		}

		if (typeof value !== "string") return setFilter(category, value);
	};

	const filters = form.watch();

	return {
		filters,
		setFilter,
		setBulkFilters,
		removeFilter,
		clearAllFilters,
	};
};

export const createInitialFilters = (): FiltersState => ({
	genetics: undefined,
	thc: undefined,
	cbd: undefined,
	effects: undefined,
	complaints: undefined,
	flavors: undefined,
	terpenes: undefined,
	irradiation: undefined,
	producers: undefined,
	topProducersLowPrice: false,
	category: undefined,
	rating: undefined,
	expertReview: undefined,
});

export const getTopProducerIds = (config: Pick<FiltersConfig, "producers">) =>
	config.producers.filter((p) => p.isTopProducer).map((p) => p.id);

const useFiltersTracking = (filterConfig: FiltersConfig) => {
	const initialFilters = createInitialFilters();

	const getRemovedFilters = useCallback(
		(prevFilters: FiltersState, filters: FiltersState) => {
			const removedFilters: FiltersState = {};

			for (const key of Object.keys(filters) as (keyof FiltersState)[]) {
				const prevValue = prevFilters[key];
				const newValue = filters[key];

				if (isEqual(newValue, prevValue)) continue;

				const isArrayField =
					Array.isArray(prevValue) && Array.isArray(newValue);
				const valuesRemovedFromArray = isArrayField
					? difference<(typeof prevValue)[number]>(prevValue, newValue)
					: [];
				const isFilterRestoredToInitialValue = isEqual(
					newValue,
					initialFilters[key],
				);

				if (isFilterRestoredToInitialValue || valuesRemovedFromArray.length) {
					const removedValue = valuesRemovedFromArray.length
						? valuesRemovedFromArray
						: prevValue;

					(removedFilters as Record<typeof key, unknown>)[key] = removedValue;
				}
			}

			return removedFilters;
		},
		[initialFilters],
	);

	const trackFiltersRemoved = useCallback(
		(
			prevFilters: FiltersState,
			filters: FiltersState,
			removedAllFilters = false,
		) => {
			const removedFilters = getRemovedFilters(prevFilters, filters);

			if (!Object.keys(removedFilters).length) return;

			captureEvent(
				productListFiltersRemovedEvent(
					removedFilters,
					filterConfig,
					removedAllFilters,
				),
			);
		},
		[filterConfig, getRemovedFilters],
	);

	return {
		trackFiltersRemoved,
	};
};
